import { FormInputState, FormInputValidation } from "../../utils/validations"

export const stripeFields = ["cardHolderEmail", "cardStripe", "cardHolderName"]
export const passwordFields = ["password", "passwordConfirm"]
export const userFields = ["email", "firstName", "lastName"]
export const vatFields = ["shippingVat", "billingVat"]
export const shippingFields = [
  "shippingFirstName",
  "shippingLastName",
  "shippingStreet",
  "shippingUnit",
  "shippingPhone",
  "shippingCity",
  "shippingProvince",
  "shippingCountry",
  "shippingPostal",
  "shippingVat",
]
export const billingFields = [
  "billingStreet",
  "billingUnit",
  "billingPhone",
  "billingCity",
  "billingProvince",
  "billingCountry",
  "billingPostal",
]
export const giftFields = ["giftFirstName", "giftLastName", "giftEmail"]

export const allFields = [
  ...userFields,
  ...passwordFields,
  ...shippingFields,
  ...billingFields,
  ...stripeFields,
  ...vatFields,
  ...giftFields,
]

const requiredValidation = {
  validation: [{ type: "required" } as FormInputValidation],
}

const phoneValidation = {
  validation: [
    { type: "required" } as FormInputValidation,
    { type: "latin" } as FormInputValidation,
    { type: "phone" } as FormInputValidation,
  ],
}

const latinRequiredValidation = {
  validation: [
    { type: "required" } as FormInputValidation,
    { type: "latin" } as FormInputValidation,
  ],
}

export const shippingFormInputs: Record<string, FormInputState> = {
  shippingFirstName: { label: "First Name", value: "", ...requiredValidation },
  shippingLastName: { label: "Last Name", value: "", ...requiredValidation },
  shippingStreet: {
    label: "Shipping Street",
    value: "",
    ...latinRequiredValidation,
  },
  shippingUnit: { label: "Shipping Unit", value: "" },
  shippingPhone: { label: "Shipping Phone", value: "", ...phoneValidation },
  shippingCity: {
    label: "Shipping City",
    value: "",
    ...latinRequiredValidation,
  },
  shippingProvince: {
    label: "Shipping State / Province",
    value: "",
    ...latinRequiredValidation,
  },
  shippingCountry: {
    label: "Shipping Country",
    value: "",
    ...latinRequiredValidation,
  },
  shippingPostal: {
    label: "Shipping Zip / Postal",
    value: "",
    validation: [
      { type: "required" } as FormInputValidation,
      { type: "postal", otherField: "shippingCountry" } as FormInputValidation,
    ],
  },
  shippingVat: {
    label: "Vat Number",
    value: "",
    validation: [],
  },
}

export const billingFormInputs: Record<string, FormInputState> = {
  // billingFirstName: { label: "First Name", value: "", ...requiredValidation },
  // billingLastName: { label: "Last Name", value: "", ...requiredValidation },
  billingStreet: {
    label: "Billing Street",
    value: "",
    ...latinRequiredValidation,
  },
  billingUnit: { label: "Billing Unit", value: "" },
  billingPhone: {
    label: "Billing Phone",
    value: "",
    ...phoneValidation,
  },
  billingCity: { label: "Billing City", value: "", ...latinRequiredValidation },
  billingProvince: {
    label: "Billing Province",
    value: "",
    ...latinRequiredValidation,
  },
  billingCountry: {
    label: "Billing Country",
    value: "",
    ...latinRequiredValidation,
  },
  billingPostal: {
    label: "Billing Postal",
    value: "",
    validation: [
      { type: "required" } as FormInputValidation,
      { type: "postal", otherField: "billingCountry" } as FormInputValidation,
    ],
  },
}
export const paymentFormInputs: Record<string, FormInputState> = {
  cardHolderName: {
    value: "",
    label: "Cardholder Name",
    validation: [
      { type: "required" },
      { type: "min", value: 3 },
      { type: "max", value: 150 },
    ],
  },
  cardHolderEmail: {
    value: "",
    label: "Cardholder Email",
    validation: [
      { type: "required" },
      { type: "email" },
      { type: "min", value: 3 },
      { type: "max", value: 150 },
    ],
  },
  cardStripe: {
    value: "",
    label: "Payment Method",
    complete: false,
    validation: [{ type: "complete" }],
  },
}

export const userFormInputs: Record<string, FormInputState> = {
  firstName: {
    value: "",
    label: "First Name",
    validation: [
      { type: "required" },
      { type: "min", value: 2 },
      { type: "max", value: 150 },
    ],
  },
  lastName: {
    value: "",
    label: "Last Name",
    validation: [
      { type: "required" },
      { type: "min", value: 2 },
      { type: "max", value: 150 },
    ],
  },
  email: {
    value: "",
    label: "Email",
    validation: [
      { type: "required" },
      { type: "email" },
      { type: "min", value: 3 },
      { type: "max", value: 150 },
    ],
  },
}

export const confirmGiftInput: Record<string, FormInputState> = {
  giftEmail: {
    value: "",
    label: "Email",
    validation: [
      { type: "required" },
      { type: "email" },
      { type: "min", value: 3 },
      { type: "max", value: 150 },
    ],
  },
  giftToken: {
    value: "",
    label: "Gift Code",
    validation: [
      { type: "required" },
      { type: "min", value: 12 },
      { type: "max", value: 12 },
    ],
  },
}

export const initialFormInput: Record<string, FormInputState> = {
  ...userFormInputs,
  password: {
    value: "",
    label: "Password",
    validation: [
      { type: "required" },
      { type: "min", value: 5 },
      { type: "max", value: 150 },
    ],
  },
  passwordConfirm: {
    value: "",
    label: "Confirm Password",
    validation: [
      { type: "required" },
      { type: "min", value: 5 },
      { type: "max", value: 150 },
      { type: "match", value: "password" },
    ],
  },
  ...paymentFormInputs,
  ...shippingFormInputs,
  ...billingFormInputs,
  giftFirstName: {
    value: "",
    label: "Gift Recipient First Name",
    validation: [{ type: "required" }],
  },
  giftLastName: {
    value: "",
    label: "Gift Recipient Last Name",
    validation: [{ type: "required" }],
  },
  giftEmail: {
    value: "",
    label: "Gift Recipient Email",
    validation: [
      { type: "required" },
      { type: "email" },
      { type: "min", value: 3 },
      { type: "max", value: 150 },
    ],
  },
}
